import { useParams } from "@tanstack/react-router";
import { Download, FileAudio, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

interface TranscriptCue {
  start: number;
  end: number;
  speaker: string;
  text: string;
}

const MOCK = {
  id: "art_abc123",
  title: "Audio overview — NetBox onboarding",
  type: "audio" as const,
  duration: 285,
  created_at: "2026-05-15T07:30:00Z",
  cost_usd: 0.18,
  model: "@cf/myshell-ai/melotts",
  source_doc_ids: ["doc_netbox_manual", "doc_netbox_deploy", "doc_cf_access"],
  audio_url: "https://upload.wikimedia.org/wikipedia/commons/4/47/Sound_Effect_-_Wood_Knock.ogg",
  transcript: [
    {
      start: 0,
      end: 8,
      speaker: "Anfitrión",
      text: "Hola, en este briefing repasamos cómo desplegar NetBox en staging usando Cloudflare Access.",
    },
    {
      start: 8,
      end: 22,
      speaker: "Anfitrión",
      text: "Primero, configuramos los túneles de acceso a la red interna. Esto está documentado en el manual oficial.",
    },
    {
      start: 22,
      end: 38,
      speaker: "Anfitrión",
      text: "Después, registramos la aplicación en Access con los emails autorizados de @iort.io y servicio token CI.",
    },
    {
      start: 38,
      end: 55,
      speaker: "Anfitrión",
      text: "El despliegue propiamente dicho se hace con Pulumi: un stack staging crea D1, R2 y los workers necesarios.",
    },
    {
      start: 55,
      end: 75,
      speaker: "Anfitrión",
      text: "Una vez aplicado, ejecutamos las migraciones y desplegamos los workers que requieren binding AI Search vía wrangler.",
    },
    {
      start: 75,
      end: 95,
      speaker: "Anfitrión",
      text: "El smoke test verifica que health, search y ask responden con auth_mode correcto.",
    },
    {
      start: 95,
      end: 115,
      speaker: "Anfitrión",
      text: "En caso de error de Pulumi por el binding AI Search, redesplegamos los workers afectados manualmente.",
    },
    {
      start: 115,
      end: 135,
      speaker: "Anfitrión",
      text: "Para producción, el flujo es idéntico pero requiere approval manual en GitHub Actions.",
    },
  ] as TranscriptCue[],
};

export function ArtifactPage() {
  const { artifactId } = useParams({ strict: false }) as { artifactId: string };
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    function onTime() {
      setT(a?.currentTime ?? 0);
    }
    function onPlay() {
      setPlaying(true);
    }
    function onPause() {
      setPlaying(false);
    }
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, []);

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => toast.error("No se pudo reproducir"));
    } else {
      a.pause();
    }
  }

  function jumpTo(s: number) {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = s;
    a.play().catch(() => undefined);
  }

  const activeIdx = MOCK.transcript.findIndex((c) => t >= c.start && t < c.end);

  return (
    <div className="mx-auto max-w-4xl px-6 py-6">
      <header className="mb-4 flex items-center gap-2">
        <FileAudio size={20} />
        <h1 className="text-2xl font-semibold">{MOCK.title}</h1>
        <Badge variant="default" className="ml-2">
          {MOCK.type}
        </Badge>
      </header>
      <p className="text-xs text-(--text-tertiary)">
        ID {artifactId} · modelo {MOCK.model} · coste ${MOCK.cost_usd.toFixed(2)} · {MOCK.duration}s
        · creado {new Date(MOCK.created_at).toLocaleString()}
      </p>
      <p className="mt-1 text-xs text-(--text-tertiary)">
        Fuentes: {MOCK.source_doc_ids.length} documentos · transcript indexado y consultable por
        chat
      </p>

      <section className="mt-4 rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4">
        <audio ref={audioRef} src={MOCK.audio_url}>
          <track kind="captions" />
        </audio>
        <div className="flex items-center gap-3">
          <Button variant="primary" size="md" onClick={toggle}>
            {playing ? <Pause size={16} /> : <Play size={16} />}
            {playing ? "Pausar" : "Reproducir"}
          </Button>
          <div className="flex-1">
            <input
              type="range"
              min={0}
              max={MOCK.duration}
              step={1}
              value={t}
              onChange={(e) => jumpTo(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-(--text-tertiary)">
              <span>{fmt(t)}</span>
              <span>{fmt(MOCK.duration)}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            <Download size={14} /> Descargar
          </Button>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-semibold">Transcript sincronizado</h2>
        <ol className="space-y-1">
          {MOCK.transcript.map((c, i) => (
            <li key={`${c.start}`}>
              <button
                type="button"
                onClick={() => jumpTo(c.start)}
                className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                  i === activeIdx
                    ? "bg-(--accent-50) text-(--accent-700)"
                    : "hover:bg-(--bg-hover) text-(--text-secondary)"
                }`}
              >
                <span className="mr-2 inline-block w-12 font-mono text-xs text-(--text-tertiary)">
                  {fmt(c.start)}
                </span>
                <strong className="mr-1">{c.speaker}:</strong>
                {c.text}
              </button>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function fmt(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}
